/**
 * HuaweiBridgeClient encapsulates the JS Bridge communication between
 * Web and HarmonyOS native. It provides a Promise-based `call()` API
 * that internally serializes requests and matches them with async
 * native responses.
 *
 * Native side injects `window.aigensHuawei` object via javaScriptProxy.
 * Responses come back via `window.aigensHuaweiCallback(id, result)`
 * and `window.aigensHuaweiError(id, error)`.
 */
export class HuaweiBridgeClient {
    constructor() {
        this.pendingCallbacks = new Map();
        this.callbackCounter = 0;
        this.initialized = false;
    }
    /**
     * Singleton access. The bridge client is global by nature
     * (one WebView = one native bridge).
     */
    static getInstance() {
        if (!HuaweiBridgeClient._instance) {
            HuaweiBridgeClient._instance = new HuaweiBridgeClient();
        }
        return HuaweiBridgeClient._instance;
    }
    /**
     * Register global callback handlers on window so native can
     * invoke them via evaluateJavaScript.
     * Called lazily on first `call()`.
     */
    ensureInitialized() {
        if (this.initialized)
            return;
        window.aigensHuaweiCallback = (id, result) => {
            const pending = this.pendingCallbacks.get(id);
            if (pending) {
                this.pendingCallbacks.delete(id);
                pending.resolve(result);
            }
        };
        window.aigensHuaweiError = (id, error) => {
            const pending = this.pendingCallbacks.get(id);
            if (pending) {
                this.pendingCallbacks.delete(id);
                pending.reject(new Error(error));
            }
        };
        this.initialized = true;
    }
    /**
     * Check whether the Huawei bridge is available (injected by native).
     */
    isBridgeAvailable() {
        return typeof window !== 'undefined' && !!window.aigensHuawei && typeof window.aigensHuawei.postMessage === 'function';
    }
    /**
     * Call a native method via JS Bridge.
     *
     * @param method Method name (must match BridgeMethod)
     * @param params Optional parameters object
     * @returns Promise that resolves with native response result
     */
    call(method, params) {
        return new Promise((resolve, reject) => {
            if (!this.isBridgeAvailable()) {
                reject(new Error(`Huawei bridge not available: window.aigensHuawei is missing`));
                return;
            }
            this.ensureInitialized();
            const id = this.generateCallbackId();
            const request = { id, method, params: params || {} };
            // Store pending callback before sending to avoid race conditions
            this.pendingCallbacks.set(id, { resolve, reject });
            try {
                const json = JSON.stringify(request);
                window.aigensHuawei.postMessage(json);
            }
            catch (e) {
                this.pendingCallbacks.delete(id);
                reject(new Error(`Failed to send bridge message: ${e.message}`));
            }
        });
    }
    /**
     * Generate a unique callback ID using timestamp + counter.
     * Lightweight alternative to UUID for performance.
     */
    generateCallbackId() {
        this.callbackCounter++;
        return `cb_${Date.now()}_${this.callbackCounter}`;
    }
}
HuaweiBridgeClient._instance = null;
//# sourceMappingURL=huawei-bridge-client.js.map